<template>
  <div>

    <div class="box" :class="{ 'py-1 px-1': compact }">
      <div class="flex flex-wrap items-center" v-if="showActions">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getPayments" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <button
            v-if="hasPermission('payment.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.payment.enabled"
            @click="exportPayments"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.payment.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getPayments">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>

      </div>

      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <payment-filters
            :filters.sync="filters"
            @updated="getPayments"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasPayments">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th v-if="!compact">#</th>
                  <th>Account</th>
                  <th>Created for</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Method</th>
                  <th v-if="!compact">Note</th>
                  <th>Created at</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(payment, i) in payments" :key="i">
                  <td class="font-bold" v-if="!compact">{{ payment.id }}</td>
                  <td>
                    <template v-if="payment.account && payment.account.id">
                      <a class="text-pink-500" v-if="isAdmin" @click.stop="$router.push('/administration/financial-account/details/' + payment.account.id)">
                        {{ payment.account.name }}
                        <span v-if="payment.account.deleted_at" class="text-red-500 small">(deleted)</span>
                      </a>
                      <span v-else>
                        {{ payment.account.name }}
                        <span v-if="payment.account.deleted_at" class="text-red-500 small">(deleted)</span>
                      </span>
                    </template>
                  </td>
                  <td>
                    <a class="text-link" @click.stop="$router.push('/credit-note/preview/' + payment.credit_note.id)" v-if="payment.credit_note && payment.credit_note.id">
                      <span>Credit note #{{ payment.credit_note.id }}</span>
                      <p class="text-sm">
                        {{ payment.credit_note.contact.first_name }}
                        {{ payment.credit_note.contact.last_name }}
                        <span v-if="!compact && payment.credit_note.contact.company_name">
                          ({{ payment.credit_note.contact.company_name }})
                        </span>
                      </p>
                    </a>
                    <a class="text-link" @click.stop="$router.push('/invoice/preview/' + payment.invoice.id)" v-else-if="payment.invoice && payment.invoice.id">
                      <span>Invoice #{{ payment.invoice.id }}</span>
                      <p class="text-sm">
                        {{ payment.invoice.contact.first_name }}
                        {{ payment.invoice.contact.last_name }}
                        <span v-if="!compact && payment.invoice.contact.company_name">
                          ({{ payment.invoice.contact.company_name }})
                        </span>
                      </p>
                      <p class="text-sm" v-if="payment.invoice.job && payment.invoice.job.job_type && payment.invoice.job.vehicle">
                        {{ payment.invoice.job.job_type.name }} |
                        <span>{{ payment.invoice.job.vehicle.make }} {{ payment.invoice.job.vehicle.model }}</span>
                      </p>
                    </a>
                  </td>
                  <td>
                    <span :class="payment.typeClass">{{ payment.type }}</span>
                  </td>
                  <td>{{ $formatMoney(payment.value) }}</td>
                  <td>{{ payment.method }}</td>
                  <td v-if="!compact">{{ payment.note }}</td>
                  <td>
                    {{ $moment(payment.created_at).format(config.dateTimeFormats.display) }}
                  </td>
                  <td>
                    <span :class="payment.statusClass">{{ payment.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasPayments && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const PaymentFilters = resolve => require(['@/components/filters/Filters'], resolve)

export default {
  props: {
    // invoiceID: {}, // for future use
    // accountID: {}, // for future use
    showActions: {
      type: Boolean,
      default: true,
    },
    method: String,
    compact: Boolean,
  },
  components: {
    FilteringToggleButton,
    ListSearch,
    PaginationComponent,
    PaymentFilters,
  },
  data() {
    return {
      payments: [],
      searchInput: '',
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        status: {
          key: 'status',
          name: 'Status',
          active: true,
          options: [
            {value: 'paid', class: 'btn-green'},
            {value: 'unpaid', class: 'btn-red'},
            {value: 'cancelled', class: 'btn-red'},
          ],
          value: null,
        },
        type: {
          key: 'type',
          name: 'Type',
          active: true,
          options: [
            {value: 'income', class: 'btn-green'},
            {value: 'expense', class: 'btn-red'},
            {value: 'deposit', class: 'btn-orange'},
          ],
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      exportFile: {
        payment: {
          enabled: true,
        }
      },
      query: {
        search: '',
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getPayments()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getPayments()
      }
    },
  },
  computed: {
    hasPayments() {
      return Object.entries(this.payments).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      isAdmin: 'user/isAdmin',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.getPayments()
  },
  methods: {
    exportPayments() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      this.exportFile.payment.enabled = false
      axios.get('payments/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.payment.enabled = true
        })
    },
    getPayments() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      axios.get('payments', {params: {
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.payments = Object.assign([], res.data)
            this.payments.map(p => {
              if (p.type === 'income') {
                p.typeClass = 'text-green'
              } else if (p.type === 'expense') {
                p.typeClass = 'text-red'
              } else if (p.type === 'deposit') {
                p.typeClass = 'text-orange'
              }
              if (p.status === 'paid') {
                p.statusClass = 'text-green'
              } else if (p.status === 'unpaid' || p.status === 'cancelled') {
                p.statusClass = 'text-red-500'
              }
            })

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
